import { InputConfig } from '@controls/advanced-controls/advanced-inputs.model';
import { Column, DataType, InputType } from '@miq/catalog';

/**
 * Basic union between compatible types converging in services
 */
export type InputTypeInputConfigUnion = Pick<
  Column & InputConfig,
  'inputType' | 'dataType' | 'lookupCategory' | 'values'
>;

const inputTypeDataTypeMap = new Map<string, InputType>([
  [`${InputType.Slider}-${DataType.Boolean}`, InputType.Checkbox],
  [`${InputType.Date}-${DataType.String}`, InputType.Date],
  [`${InputType.Date}-${DataType.Text}`, InputType.Date],
  [`${InputType.Date}-${DataType.Date}`, InputType.Date],
  [`${InputType.Date}-${DataType.Timestamp}`, InputType.Date],
  [`${InputType.Date}-${DataType.TimestampWithTimeZone}`, InputType.Date],
  [`${InputType.Text}-${DataType.Timestamp}`, InputType.Date],
  [`${InputType.Text}-${DataType.TimestampWithTimeZone}`, InputType.Date],
  [`${InputType.Text}-${DataType.Date}`, InputType.Date],

  [`${InputType.Date}-${DataType.Id}`, InputType.Number],
  [`${InputType.Date}-${DataType.Integer}`, InputType.Number],
  [`${InputType.Date}-${DataType.Numeric}`, InputType.Number],
  [`${InputType.Number}-${DataType.Month}`, InputType.Number],
  [`${InputType.Number}-${DataType.Integer}`, InputType.Number],
  [`${InputType.Number}-${DataType.Numeric}`, InputType.Number],
  [`${InputType.Number}-${DataType.Id}`, InputType.Number],
  [`${InputType.Number}-${DataType.Date}`, InputType.Number],
  [`${InputType.Text}-${DataType.Numeric}`, InputType.Number],
  [`${InputType.Text}-${DataType.Integer}`, InputType.Number],
  [`${InputType.Slider}-${DataType.Numeric}`, InputType.Number],
  [`${InputType.Slider}-${DataType.Integer}`, InputType.Number],

  [`${InputType.Text}-${DataType.Id}`, InputType.Text],
  [`${InputType.Text}-${DataType.Text}`, InputType.Text],
  [`${InputType.Slider}-${DataType.Text}`, InputType.Text],
  [`${InputType.Text}-${DataType.TextArea}`, InputType.TextArea],
  [`${InputType.Year}-${DataType.Id}`, InputType.Year],
]);

/**
 * Return AdvancedInput component based on DataType InputType and lookupCategory mix
 * @param inputType
 * @param lookupCategory
 */
export function getByDataAndInputType({ inputType, dataType, lookupCategory }: InputTypeInputConfigUnion): InputType {
  if (lookupCategory != null && inputType !== InputType.NAICS) {
    return InputType.Dropdown;
  }

  return inputTypeDataTypeMap.get(`${inputType}-${dataType}`) ?? null;
}

/**
 * Return AdvancedInput component based on InputType and lookupCategory
 * @param inputType
 * @param lookupCategory
 */
export function getByInputType({ inputType, lookupCategory, values }: InputTypeInputConfigUnion): InputType {
  switch (inputType) {
    // CHIPS
    case InputType.Chips:
    case InputType.StrictChips:
    case InputType.Array:
      return InputType.Chips;
    case InputType.NAICS:
      return InputType.NAICS;
    // Dropdown
    case InputType.Dropdown:
      return lookupCategory != null || (Array.isArray(values) && values.length > 0)
        ? InputType.Dropdown
        : InputType.Text;
  }
  return null;
}

/**
 * Return AdvancedInput component based on DataType only
 * @param inputType
 * @param lookupCategory
 */
export function getByDataType({ dataType }: InputTypeInputConfigUnion): InputType {
  switch (dataType) {
    // CHIPS
    case DataType.Numeric:
      return InputType.Number;
    case DataType.TextArea:
      return InputType.TextArea;
    case DataType.TimestampWithTimeZone:
      return InputType.Date;
  }
  return null;
}

/**
 * AdvancedInputFactory
 */
export class AdvancedInputFactory {
  static getInputType(params: InputTypeInputConfigUnion): InputType {
    return (
      getByDataAndInputType(params) ||
      getByInputType(params) ||
      getByDataType(params) ||
      // defaulted to Text
      InputType.Text
    );
  }
}
